/**
 * Pane C — README "4. Context + AI pane (pane C)" and communicatorv2.3.md §7
 * ("Pane C is the on-ramp… it must be built against the operations layer,
 * not the UI").
 *
 * ## Where each field comes from (fix round 2 — see task-42-report.md)
 *
 * This pane's `ThreadDetail` now comes from `../../lib/useThreadDetail.ts` —
 * read that module's header comment for the full story. Short version: this
 * component used to fetch its own independent copy of every field via
 * `ops.thread()`, keyed only on `activeId`, so a mutation that changed the
 * thread WITHOUT changing which thread was selected (attach a ticket,
 * another dispatcher closing it, a hand-off) never reached this pane at all
 * — the store had the truth, this component was still showing whatever it
 * fetched at selection time. That was the reported bug: "link-ticket
 * succeeded but the pane still shows the unlinked state, so there is no
 * Force sync button" — the same shape as three other fixes that landed the
 * same night (the coverage toggle, the role segment, the client transport),
 * and the same shape `ThreadPane`'s transcript turned out to share (a
 * separate report the same night: the other party's message never appeared
 * without a hard refresh, for the identical reason). `useThreadDetail` is
 * the one place that shape got fixed, for both panes at once.
 *
 * The AI tab's seam read (`aiOps.read()`) is unrelated to any of this — see
 * its own effect below for why it only fires while the AI tab is open.
 *
 * Force sync calls the force-sync operation directly (allowed per
 * task-16-brief.md). Change ticket / Attach a ticket only open modal state —
 * building the modal itself is Task 17.
 */
import { useEffect, useRef, useState } from 'react'
import { ops } from '../../lib/ops'
import type { ThreadSummary } from '@communicator/shared'
import { useStore } from '../../store'
import type { CtxTab } from '../../store'
import { useThreadDetail } from '../../lib/useThreadDetail'
import { activateOnKey } from '../rail/format'
import { Icon, Toggle } from '../ui'
import { AI_LOADING_STATE, AI_NO_THREAD_STATE, AI_SEAM_STATE, aiOps } from './aiOps'
import type { AiPaneRead } from './aiOps'
import { clientPresence } from './clientPresence'
import { AiTab } from './AiTab'
import { CollapsedRail } from './CollapsedRail'
import { ContextTab } from './ContextTab'

function TabButton({ label, active, onClick, testId }: { label: string; active: boolean; onClick: () => void; testId: string }) {
  return (
    <div
      role="button"
      tabIndex={0}
      data-testid={testId}
      onClick={onClick}
      onKeyDown={(e) => activateOnKey(e, onClick)}
      className={[
        'cursor-pointer rounded-[7px] px-2.5 py-1 text-[11px] font-semibold',
        active ? 'bg-cc-blue-pale text-cc-blue-text' : 'text-lo',
      ].join(' ')}
    >
      {label}
    </div>
  )
}

export function ContextPane() {
  const ctxOpen = useStore((s) => s.ctxOpen)
  const ctxTab = useStore((s) => s.ctxTab)
  const activeId = useStore((s) => s.activeId)
  // The active thread's last activity, as a re-read trigger for the AI tab.
  // A new inbound text updates this; without it the suggestions sat frozen on
  // whatever the conversation looked like when the tab was opened, which is
  // exactly when they stop being worth reading.
  // `threads` is an array, so this finds rather than indexes. The selector
  // returns a primitive, which keeps zustand from re-rendering on every store
  // write that leaves this thread's activity unchanged.
  const activeThread = useStore((s) => s.threads.find((t) => t.id === s.activeId) ?? null)
  const upsertThread = useStore((s) => s.upsertThread)
  const activeLastActivity = useStore(
    (s) => s.threads.find((t) => t.id === s.activeId)?.lastActivity ?? null,
  )
  const haloUp = useStore((s) => s.svc.halo)
  const set = useStore((s) => s.set)
  const selectThread = useStore((s) => s.selectThread)
  const openModal = useStore((s) => s.openModal)

  // See ../../lib/useThreadDetail.ts's own header comment for exactly which
  // fields come from the live store vs. the ops-only fetch, and why.
  const { detail: mergedDetail, refetch } = useThreadDetail(activeId)

  const [aiRead, setAiRead] = useState<AiPaneRead>(AI_SEAM_STATE)
  /**
   * What we have already been told, per thread and per last-activity stamp.
   *
   * The server caches too, but that is still a round trip and — until this —
   * a visible three-second gap on a conversation whose advice had not changed.
   * Held for the session: switching threads and coming back is instant.
   */
  const aiCache = useRef<Map<string, AiPaneRead>>(new Map())
  const [aiViewedIds, setAiViewedIds] = useState<ReadonlySet<string>>(new Set())

  // Design 2026-08-24 (client-version-visibility): the contact's
  // last-reported client app version, for the Channel row's " — v<version>"
  // annotation (./clientPresence.ts). Chat-only — this is the client desktop
  // app's own version, which has no meaning for a voice call or an SMS
  // thread with no relay session behind it at all.
  const [clientVersion, setClientVersion] = useState<string | null>(null)

  // The AI tab's read — see ./aiOps.ts.
  //
  // Only runs while the AI tab is actually showing: opening it, or already
  // having it open when a different thread is selected. Sitting on the Context
  // tab never fires it, which matters because this one costs money.
  //
  // `activeLastActivity` is in the deps so a new message re-reads. Re-reading
  // is close to free and always correct: the route caches on the thread's LAST
  // MESSAGE ID, so a trigger that fires without a new message is a cache hit
  // and never reaches Anthropic — while a real new message misses the cache
  // and produces advice about the message that just arrived.
  useEffect(() => {
    let cancelled = false
    if (!activeId) {
      // Nothing open: say so, rather than the seam's "not wired", which told
      // everyone the feature was broken before they had clicked anything.
      setAiRead(AI_NO_THREAD_STATE)
      return
    }

    // Keyed by last activity as well as thread, so a new message misses and a
    // revisit hits — the same rule the server's own cache uses.
    const key = `${activeId}:${activeLastActivity ?? ''}`
    const hit = aiCache.current.get(key)
    if (hit) {
      setAiRead(hit)
      return
    }

    // Honest while thinking. Showing the seam here — "appears once the AI
    // operator is wired" — read as "this feature does not exist" for the
    // three seconds a first read takes.
    setAiRead(AI_LOADING_STATE)
    aiOps
      .read(activeId)
      .then((r) => {
        // Only a real answer is worth remembering; a failure must be retried
        // next time rather than cached as the truth.
        if (r.wired) aiCache.current.set(key, r)
        if (!cancelled) setAiRead(r)
      })
      .catch(() => {
        if (!cancelled) setAiRead(AI_SEAM_STATE)
      })
    return () => {
      cancelled = true
    }
  }, [activeId, activeLastActivity])

  useEffect(() => {
    if (ctxTab === 'ai' && activeId && !aiViewedIds.has(activeId)) {
      setAiViewedIds((prev) => new Set(prev).add(activeId))
    }
  }, [ctxTab, activeId, aiViewedIds])

  // Client-presence read for the Channel row — chat threads only. Runs
  // whether the Context or AI tab is open (the row is on the Context tab,
  // but there is no reason to make a dispatcher switch tabs first just to
  // trigger the fetch, and this is a cheap relay read, not an AI spend like
  // the tab-gated read above).
  useEffect(() => {
    let cancelled = false
    if (!activeId || mergedDetail?.channel !== 'chat') {
      setClientVersion(null)
      return
    }
    clientPresence
      .read(activeId)
      .then((r) => {
        if (cancelled) return
        setClientVersion(r.wired && typeof r.version === 'string' ? r.version : null)
      })
      .catch(() => {
        if (!cancelled) setClientVersion(null)
      })
    return () => {
      cancelled = true
    }
  }, [activeId, mergedDetail?.channel])

  function handleForceSync(): void {
    if (!activeId) return
    ops
      .call('force-sync', { threadId: activeId })
      .then(() => refetch())
      .catch(() => {})
  }

  function handleChangeTicket(): void {
    openModal('ticket')
  }

  function handleAttachTicket(): void {
    openModal('ticket')
  }

  // "Not her? Change" (README §5) reopens identity matching — the same
  // Match modal ThreadHeader's own tier badge already opens (ThreadPane.tsx's
  // `handleMatch`). Duplicated here rather than threaded through as a prop
  // from ThreadPane, since this pane already owns `openModal` directly and
  // the two panes are siblings, not parent/child.
  function handleChangeIdentity(): void {
    openModal('match')
  }

  function handleExpandTab(tab: CtxTab): void {
    set({ ctxOpen: true, ctxTab: tab })
  }

  const aiUnread = activeId !== null && !aiViewedIds.has(activeId)

  if (!ctxOpen) {
    return (
      <CollapsedRail
        onExpand={() => set({ ctxOpen: true })}
        onExpandContextTab={() => handleExpandTab('context')}
        onExpandAiTab={() => handleExpandTab('ai')}
        aiUnread={aiUnread}
      />
    )
  }

  return (
    // README §Layout: "pane C ctx 300px fixed, 1px left border" — up from
    // 264px pre-repaint. The left border already lives on the sibling panes'
    // own right border (`QueueRail`) or is implicit against `ThreadPane`;
    // kept here too for the case neither neighbour supplies one.
    <div data-testid="context-pane" className="flex h-full w-[300px] flex-none flex-col border-l border-subtle bg-sidebar">
      <div className="flex flex-none items-center gap-1.5 border-b border-subtle px-3 py-2.5">
        <span className="text-[11px] font-semibold tracking-[.08em] text-mid">CONTEXT</span>
        <div className="flex-1" />
        {/* task 5 binding decision 4: the "»" collapse control becomes the
            `panel-left` icon. */}
        <div
          role="button"
          tabIndex={0}
          data-testid="context-collapse-btn"
          onClick={() => set({ ctxOpen: false })}
          onKeyDown={(e) => activateOnKey(e, () => set({ ctxOpen: false }))}
          title="Collapse context"
          className="flex h-6 w-6 cursor-pointer items-center justify-center rounded-control border border-subtle text-mid"
        >
          <Icon name="panel-left" size={14} />
        </div>
      </div>

      {/* ONE COLUMN, not two panes. Splitting the rail into fixed halves put
          the hints at a fixed 60% offset regardless of how short the context
          was — so the priority chip sat below the fold on a thread with barely
          any context, and had to be scrolled to for no reason. They now follow
          the context content directly, and the whole rail scrolls once. */}
      <div className="min-h-0 flex-1 overflow-y-auto p-3">
        <ContextTab
          detail={mergedDetail}
          haloUp={haloUp}
          clientVersion={clientVersion}
          onSelectSibling={selectThread}
          onForceSync={handleForceSync}
          onChangeTicket={handleChangeTicket}
          onAttachTicket={handleAttachTicket}
          onChangeIdentity={handleChangeIdentity}
        />

        <div className="mt-3 border-t border-subtle pt-3">
          {/* The switch sits on the title row because it governs everything
              under it. PER CONVERSATION, and shared: every read costs money and
              most conversations do not need one, so switching it off here
              switches it off for whoever opens this thread next — the
              alternative is a colleague quietly re-billing for hints somebody
              already judged useless. A new thread starts on again. */}
          <div className="mb-2 flex items-center justify-between gap-2">
            {/* README §5's HINTS block: "`sparkles` icon, eyebrow, switch on the title row." */}
            <div className="flex items-center gap-1.5 text-[11px] font-semibold tracking-[.08em] text-mid">
              <Icon name="sparkles" size={12} className="flex-none text-cc-blue-text" />
              AI HINTS
            </div>
            {activeId ? (
              <Toggle
                checked={activeThread?.aiHints !== false}
                aria-label="AI hints for this conversation"
                onChange={(next) => {
                  // Optimistic: the toggle is the whole interaction, and a
                  // switch that waits on a round trip before sliding reads as
                  // broken. The server's answer replaces it either way.
                  upsertThread({ ...(activeThread as ThreadSummary), aiHints: next })
                  void ops
                    .call('set-ai-hints', { threadId: activeId, enabled: next })
                    .then((r) => upsertThread((r as { thread: ThreadSummary }).thread))
                    .catch(() => {
                      // Put it back: a toggle that silently disagrees with the
                      // server is worse than one that did not move.
                      upsertThread({ ...(activeThread as ThreadSummary), aiHints: !next })
                    })
                }}
              />
            ) : null}
          </div>
          <AiTab aiRead={aiRead} enabled={activeThread?.aiHints !== false} />
        </div>
      </div>
    </div>
  )
}
